/**
 * Every timestamp the inbox shows, formatted one way.
 *
 * Chat apps show TIME for today, "Yesterday", a weekday inside the week, and a
 * date beyond that — never "5m ago", which goes stale on screen. `relative()`
 * is the one exception and is used only for "customer last wrote", where the
 * distance is the point.
 */

const sameDay = (a, b) => a.toDateString() === b.toDateString();

const yesterdayOf = (now) => {
	const d = new Date(now);
	d.setDate(now.getDate() - 1);
	return d;
};

/** "12:40", "Yesterday", "Mon", "3 Sep" — the list column. */
export const stamp = (date) => {
	if (!date) return '';
	const d = new Date(date);
	const now = new Date();
	if (sameDay(d, now)) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	if (sameDay(d, yesterdayOf(now))) return 'Yesterday';
	const days = (now - d) / 86400000;
	if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
	return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

/** "12:40" — inside a bubble. */
export const clock = (date) =>
	date ? new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

/** "Today", "Yesterday", or the full date — the day separator. */
export const dayLabel = (date) => {
	const d = new Date(date);
	const now = new Date();
	if (sameDay(d, now)) return 'Today';
	if (sameDay(d, yesterdayOf(now))) return 'Yesterday';
	return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
};

/** "3 Sep 2026, 12:40" — message info. */
export const full = (date) =>
	date
		? new Date(date).toLocaleString([], {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		: '';

/** "just now", "12 min ago", "3 h ago", "2 days ago". */
export const relative = (date, now = Date.now()) => {
	if (!date) return '';
	const diff = Math.max(0, now - new Date(date).getTime());
	const min = Math.floor(diff / 60000);
	if (min < 1) return 'just now';
	if (min < 60) return `${min} min ago`;
	const h = Math.floor(min / 60);
	if (h < 24) return `${h} h ago`;
	const d = Math.floor(h / 24);
	return d === 1 ? 'yesterday' : `${d} days ago`;
};

/** "0:42" — voice note length. */
export const mmss = (seconds) => {
	const s = Math.max(0, Math.round(seconds || 0));
	return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
